import { Button, CircularProgress } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { getAIProjectDescription as apiGetAiProjectDescription, getProject as apiGetProject } from '~/api/civilsearch';
import type { Project } from '~/common/types';
import BackButton from '../../common/BackButton';
import CivilSearchLayout from '../../common/CivilSearchLayout';
import InfoBanner from '../../requests/requestDetails/tabs/common/InfoBanner';
import { projectFilter } from './filters';
import ProjectForm from './ProjectForm';

type AssignmentStats = {
  majorityEmail: string | null;
  emails: string[];
};

export default function ProjectDetails() {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [assignmentStats, setAssignmentStats] = useState<AssignmentStats>({
    majorityEmail: null,
    emails: [],
  });

  const [aiProjectDescription, setAiProjectDescription] = useState<string | null>(null);

  const getAiProjectDescription = async () => {
    const description = await apiGetAiProjectDescription(projectId);
    setAiProjectDescription(description);
  };

  useAsyncRetry(async () => {
    await getAiProjectDescription();
  }, [projectId]);

  const assignedUsersText = useMemo(() => {
    const { majorityEmail, emails } = assignmentStats;

    if (emails.length === 0) return 'No assigned users.';
    if (emails.length === 1) return `${emails[0]} has all requests.`;

    const otherEmails = emails.filter((email) => email !== majorityEmail);
    return `${majorityEmail} has the majority of requests, but ${otherEmails.join(', ')} also have some.`;
  }, [assignmentStats]);

  const projectData = useAsyncRetry(async () => {
    try {
      const project = await apiGetProject<Project>(projectId, true);
      setProject(project);

      if (project.metadata?.dataRequests) {
        const emailCounts = new Map<string, number>();
        let maxCount = 0;
        let majorityEmail: string | null = null;

        project.metadata.dataRequests.forEach(({ assignedTo }: { assignedTo: { name: string; email: string } }) => {
          if (assignedTo?.email) {
            const count = (emailCounts.get(assignedTo.email) || 0) + 1;
            emailCounts.set(assignedTo.email, count);

            if (count > maxCount) {
              maxCount = count;
              majorityEmail = assignedTo.email;
            }
          }
        });

        setAssignmentStats({
          majorityEmail,
          emails: Array.from(emailCounts.keys()),
        });
      }
    } catch (error) {
      console.error(error);
      setError(true);
    }
  }, [projectId]);

  const handleOpenCivilSearch = () => {
    if (!project) return;

    const encodedFilters = encodeURIComponent(JSON.stringify(projectFilter(project.projectId)));
    const url = `${window.location.origin}/admin/civil-search/requests?projectId=${project.projectId}&filter-state=${encodedFilters}`;
    window.open(url, '_blank');
  };

  const handleOpenProject = () => {
    if (!project) return;

    const url = `${window.location.origin}/projects/${project.id}/utilities`;
    window.open(url, '_blank');
  };

  if (!project) {
    return <CircularProgress indeterminate />;
  }

  if (error) {
    return <div>Error fetching request details.</div>;
  }

  return (
    <CivilSearchLayout
      title={
        <div className="flex items-center space-x-2">
          <BackButton to="/projects">Back</BackButton>
          <span>Project Details - {project.name}</span>
        </div>
      }
    >
      <div className="flex h-full w-full grow flex-col justify-between overflow-y-clip">
        <InfoBanner project={project} />
        <div className="grow overflow-y-auto">
          <div className="flex max-w-[400px] flex-col gap-2">
            <ProjectForm project={project} assignedUsersText={assignedUsersText} onSuccess={projectData.retry} />
            <div className="flex items-center space-x-2">
              <Button onClick={handleOpenProject}>Open Project</Button>
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={handleOpenCivilSearch}>Open CivilSearch</Button>
            </div>
            <div className="pt-2">
              <span>AI Project Description (Alpha)</span>
              {aiProjectDescription ? (
                <div className="whitespace-pre-wrap text-xs">{aiProjectDescription}</div>
              ) : (
                <CircularProgress indeterminate />
              )}
            </div>
          </div>
        </div>
      </div>
    </CivilSearchLayout>
  );
}
